import { Reveal } from '../motion/Reveal.js';
import styles from './Stats.module.css';

export interface StatsProps {
  locale: 'fr' | 'en';
  variant?: 'default' | 'trust';
}

/**
 * Public proof points must be deterministic, factual and present in served HTML.
 * Avoid unsupported time-saved estimates here: these are product facts, not
 * survey/model assumptions.
 */
export function Stats({ locale, variant = 'default' }: StatsProps) {
  const fr = locale === 'fr';

  if (variant === 'trust') {
    const proofs = fr
      ? [
          ['Chez vous', 'Le linge ne quitte pas votre domicile.'],
          ['Même laveuse', 'Vous retrouvez la même personne à chaque visite.'],
          ['Paiement mobile', 'Mixx by Yas ou Flooz.'],
          [
            'Simple à gérer',
            'Vous pouvez suspendre ou annuler selon les conditions de votre formule.',
          ],
        ]
      : [
          ['At home', 'Your laundry never leaves your home.'],
          ['Same laundress', 'You see the same person at each visit.'],
          ['Mobile payment', 'Mixx by Yas or Flooz.'],
          ['Simple to manage', 'Pause or cancel according to your plan conditions.'],
        ];

    return (
      <section className={styles.trust} aria-labelledby="trust-title">
        <div className={styles.trustHead}>
          <Reveal as="p" className={styles.eyebrow}>
            {fr ? 'Pourquoi washed.' : 'Why washed.'}
          </Reveal>
          <Reveal as="h2" id="trust-title" className={styles.trustTitle} variant="mask">
            {fr ? 'Simple. Régulier. Chez vous.' : 'Simple. Regular. At home.'}
          </Reveal>
        </div>
        <div className={styles.proofGrid}>
          {proofs.map(([title, body], index) => (
            <Reveal as="article" key={title} className={styles.proof} i={index}>
              <h3>{title}</h3>
              <p>{body}</p>
            </Reveal>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className={styles.stats}>
      <div className={styles.grid}>
        <Reveal className={styles.stat} i={0}>
          <span className="smallcaps">{fr ? 'Le service' : 'The service'}</span>
          <div className={styles.figure}>
            <span className={styles.counter}>{fr ? 'Chez vous' : 'At home'}</span>
          </div>
          <p className={styles.desc}>
            {fr
              ? 'Votre linge est lavé sur place. Il ne quitte pas votre domicile.'
              : 'Your laundry is washed on-site. It never leaves your home.'}
          </p>
        </Reveal>

        <Reveal className={styles.stat} i={1}>
          <span className="smallcaps">{fr ? 'Le rythme' : 'The rhythm'}</span>
          <div className={styles.figure}>
            <span className={styles.counter}>1–2</span>
            <span className={styles.unit}>{fr ? 'visites / mois' : 'visits / month'}</span>
          </div>
          <p className={styles.desc}>
            {fr
              ? 'Des visites planifiées à l’avance, avec votre laveuse habituelle par défaut.'
              : 'Visits scheduled in advance, with your regular washerwoman assigned by default.'}
          </p>
        </Reveal>

        <Reveal className={styles.stat} i={2}>
          <span className="smallcaps">{fr ? 'Le paiement' : 'Payment'}</span>
          <div className={styles.figure}>
            <span className={styles.counter}>100%</span>
            <span className={styles.unit}>{fr ? 'mobile money' : 'mobile money'}</span>
          </div>
          <p className={styles.desc}>
            {fr
              ? 'Paiement à Washed par Mixx by Yas ou Flooz. Pas de règlement en espèces à la laveuse.'
              : 'Pay Washed through Mixx by Yas or Flooz. No cash handoff to the washerwoman.'}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
